import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  pointer-events: none;
`;

const Message = styled.div`
  position: relative;
  z-index: 2;
  padding: 16px 22px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  color: var(--text);
  box-shadow: 0 18px 44px var(--shadow);
  animation: celebration-message 1600ms ease-out forwards;
`;

const Title = styled.div`
  color: var(--celebration);
  font-size: 26px;
  font-weight: 850;
  line-height: 1.15;
`;

const Body = styled.div`
  margin-top: 4px;
  color: var(--text-soft);
  font-size: 15px;
  font-weight: 700;
`;

const Particle = styled.span<{
  color: string;
  delay: number;
  distance: number;
  rotate: number;
}>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: ${({ color }) => color};
  animation: celebration-particle 950ms ease-out forwards;
  animation-delay: ${({ delay }) => delay}ms;
  --particle-rotation: ${({ rotate }) => rotate}deg;
  transform:
    translate(-50%, -50%)
    rotate(${({ rotate }) => rotate}deg)
    translateY(0)
    scale(0.7);
  --particle-distance: ${({ distance }) => distance}px;
`;

type MilestoneCelebrationProps = {
  milestone: number | null;
};

const particleColors = [
  '#00c800',
  '#ffff00',
  '#de5956',
  '#5ab7ff',
  '#ff8a3d',
  '#f472b6',
];

function MilestoneCelebration({ milestone }: MilestoneCelebrationProps) {
  const [visibleMilestone, setVisibleMilestone] = useState<number | null>(null);
  const particles = useMemo(
    () =>
      Array.from({ length: 34 }, (_, index) => ({
        color: particleColors[index % particleColors.length],
        delay: (index % 6) * 28,
        distance: 82 + (index % 5) * 18,
        rotate: Math.round((360 / 34) * index),
      })),
    []
  );

  useEffect(() => {
    if (milestone === null) return;

    setVisibleMilestone(milestone);
    const timeout = window.setTimeout(() => {
      setVisibleMilestone(null);
    }, 1700);

    return () => window.clearTimeout(timeout);
  }, [milestone]);

  if (visibleMilestone === null) return null;

  return (
    <Overlay aria-live="polite" role="status">
      {particles.map((particle, index) => (
        <Particle
          color={particle.color}
          delay={particle.delay}
          distance={particle.distance}
          key={`${visibleMilestone}-${index}`}
          rotate={particle.rotate}
        />
      ))}
      <Message>
        <Title>{visibleMilestone} words cleared!</Title>
        <Body>Milestone reached</Body>
      </Message>
    </Overlay>
  );
}

export default MilestoneCelebration;
